import { Add } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { ExplanationAccordion, SortableTableCell } from 'components'
import * as controllers from 'controllers'
import { VehicleDto, VehicleImporterNoteDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import ImporterNoteAddEditDialog from './ImporterNoteAddEditDialog'
import { VehicleImporterNoteParameters } from 'parameters'
import { formatDateTime, useDebounce } from 'utils'
import { DEBOUNCE_DELAY } from 'localConstants'

interface ImporterNotesListCardProps {
  vehicle: VehicleDto
}

export default function ImporterNotesListCard({ vehicle }: ImporterNotesListCardProps) {
  const [isGettingVehicleImporterNotes, setIsGettingVehicleImporterNotes] = useState<boolean>(false)
  const [vehicleImporterNotes, setVehicleImporterNotes] = useState<VehicleImporterNoteDto[]>([])
  const [ImporterNoteAddEditDialogOpen, setImporterNoteAddEditDialogOpen] = useState<boolean>(false)
  const [isImporterNoteSaving, setIsImporterNoteSaving] = useState<boolean>(false)
  const [importerNote, setImporterNote] = useState<VehicleImporterNoteDto>(new VehicleImporterNoteDto())
  const [totalCount, setTotalCount] = useState<number>(0)

  const [parameters, setParameters] = useState<VehicleImporterNoteParameters>({
    page: 0,
    pageSize: 5,
    orderBy: 'createdDateTime',
    order: 'desc'
  })

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      getVehicleImporterNotes(vehicle.vehicleGuid, parameters)
    }
  }, [vehicle.vehicleGuid, parameters])

  const getVehicleImporterNotes = useDebounce((vehicleGuid: string, parameters: VehicleImporterNoteParameters) => {
    setIsGettingVehicleImporterNotes(true)
    controllers
      .getVehicleImporterNotes(vehicleGuid, parameters)
      .then(response => {
        setVehicleImporterNotes(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingVehicleImporterNotes(false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <>
      <ImporterNoteAddEditDialog
        initialValues={importerNote}
        isLoading={isImporterNoteSaving}
        onClose={() => {
          setImporterNoteAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsImporterNoteSaving(true)
          controllers
            .createImporterNote(values, vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Importer Note Saved Successfully!', { variant: 'success' })
              const importerNote = response.value
              setImporterNote(importerNote)
              setImporterNoteAddEditDialogOpen(false)
              getVehicleImporterNotes(vehicle.vehicleGuid, parameters)
            })
            .finally(() => {
              setIsImporterNoteSaving(false)
            })
        }}
        open={ImporterNoteAddEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={8}>
                <Typography fontWeight='bold' variant='h5'>
                  Importer Notes
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Add />}
                  onClick={() => {
                    setImporterNote(new VehicleImporterNoteDto())
                    setImporterNoteAddEditDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD
                </Button>
              </Grid>

              {isGettingVehicleImporterNotes && (
                <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                  <Grid>
                    <CircularProgress color='primary' size={50} />
                  </Grid>
                </Grid>
              )}

              {!isGettingVehicleImporterNotes && (
                <Grid item xs={12}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='createdDateTime'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Time Stamp
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='createdBy'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Entered By
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='note'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Note
                          </SortableTableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {vehicleImporterNotes.map(note => (
                          <TableRow>
                            <TableCell>{note.createdDateTime ? formatDateTime(note.createdDateTime) : ''}</TableCell>

                            <TableCell>
                              {note.createdBy?.firstName} {note.createdBy?.lastName}
                            </TableCell>

                            <TableCell>{note.note}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>

                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            count={totalCount}
                            onPageChange={(_, page) => {
                              setParameters({ ...parameters, page })
                            }}
                            onRowsPerPageChange={e => {
                              setParameters({ ...parameters, page: 0, pageSize: e.target.value as unknown as number })
                            }}
                            page={parameters.page}
                            rowsPerPage={parameters.pageSize}
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            SelectProps={{ fullWidth: false }}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              <Grid item xs={12}>
                <ExplanationAccordion variant='outlined'>
                  Notes are listed in ascending order by Time Stamp. ||Add Note opens a dialog for the user to enter a note.
                  Note Date and Entered by is automatically populated based on the current time and user signed in.
                </ExplanationAccordion>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
